import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import { update as updateRubric } from 'js/actions/rubric-actions';
import { listBreakouts } from 'js/actions/breakout-actions';
import { LoadingMessage } from 'js/components';
import { useNotify } from 'js/hooks/useNotify';
import { collectRubricAssessmentKeys } from 'js/utils/rubric-org-scores';
import formatRubricSaveError from '../components/formatRubricSaveError';
import RubricSettingsComponent from '../components/RubricSettingsComponent';

const EXCLUSION_SAVE_DEBOUNCE_MS = 500;

function unwrapBreakouts(result) {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  const body = result.data?.result ?? result.result ?? result.data ?? result;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

function resumeNavigation(history, nextLocation, action) {
  if (action === 'REPLACE') {
    history.replace(nextLocation);
  } else if (action === 'POP') {
    history.goBack();
  } else {
    history.push(nextLocation);
  }
}

function RubricSettingsContainer({
  workspaceId,
  rubricId,
  rubric,
  rubricLoadFailed,
  updateRubric,
  listBreakouts,
}) {
  const history = useHistory();
  const notify = useNotify();
  const [breakouts, setBreakouts] = useState([]);
  const [breakoutsLoading, setBreakoutsLoading] = useState(false);
  const [excludedBreakoutIds, setExcludedBreakoutIds] = useState([]);
  const initializedRubricId = useRef(null);
  const rubricRef = useRef(rubric);
  const excludedRef = useRef(excludedBreakoutIds);
  const dirtyRef = useRef(false);
  const savePromiseRef = useRef(null);
  const bypassBlockRef = useRef(false);

  rubricRef.current = rubric;
  excludedRef.current = excludedBreakoutIds;

  const assessmentKeys = useMemo(
    () => (rubric ? collectRubricAssessmentKeys(rubric.document) : []),
    [rubric]
  );

  useEffect(() => {
    if (!rubric || initializedRubricId.current === rubric.id) return;
    initializedRubricId.current = rubric.id;
    setExcludedBreakoutIds(
      (rubric.document?.excluded_breakout_ids ?? []).map(String)
    );
  }, [rubric]);

  useEffect(() => {
    let cancelled = false;
    if (!rubric || assessmentKeys.length === 0) {
      setBreakouts([]);
      setBreakoutsLoading(false);
      return undefined;
    }

    setBreakoutsLoading(true);
    listBreakouts(null, assessmentKeys)
      .then((result) => {
        if (!cancelled) setBreakouts(unwrapBreakouts(result));
      })
      .catch(() => {
        if (!cancelled) setBreakouts([]);
      })
      .finally(() => {
        if (!cancelled) setBreakoutsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rubric, assessmentKeys, listBreakouts]);

  const persistExcludedBreakouts = useCallback(async () => {
    const currentRubric = rubricRef.current;
    if (!currentRubric) return;

    try {
      await updateRubric(rubricId, {
        document: {
          ...currentRubric.document,
          excluded_breakout_ids: excludedRef.current.map(String),
        },
      });
      dirtyRef.current = false;
    } catch (err) {
      notify({
        type: 'danger',
        message: formatRubricSaveError(err),
      });
      throw err;
    }
  }, [notify, updateRubric, rubricId]);

  const saveExcludedBreakouts = useMemo(
    () =>
      debounce(() => {
        const promise = persistExcludedBreakouts();
        // Timer-fired saves may have no awaiter; avoid unhandled rejection.
        promise.catch(() => {});
        savePromiseRef.current = promise;
        promise.finally(() => {
          if (savePromiseRef.current === promise) {
            savePromiseRef.current = null;
          }
        });
        return promise;
      }, EXCLUSION_SAVE_DEBOUNCE_MS),
    [persistExcludedBreakouts]
  );

  const flushExcludedBreakouts = useCallback(async () => {
    // flush() is a no-op when nothing is queued (this lodash build has no .pending()).
    await saveExcludedBreakouts.flush();
    if (savePromiseRef.current) {
      await savePromiseRef.current;
    }
    if (dirtyRef.current) {
      await persistExcludedBreakouts();
    }
  }, [persistExcludedBreakouts, saveExcludedBreakouts]);

  useEffect(() => {
    const unblock = history.block((nextLocation, action) => {
      if (bypassBlockRef.current) {
        return undefined;
      }

      const needsFlush = dirtyRef.current || savePromiseRef.current != null;

      if (!needsFlush) {
        return undefined;
      }

      bypassBlockRef.current = true;

      flushExcludedBreakouts()
        .then(() => {
          unblock();
          resumeNavigation(history, nextLocation, action);
        })
        .catch(() => {
          // Stay put; error already notified. Re-arm for the next attempt.
          bypassBlockRef.current = false;
        });

      return false;
    });

    return () => {
      unblock();
      if (dirtyRef.current || savePromiseRef.current != null) {
        flushExcludedBreakouts().catch(() => {});
      }
    };
  }, [history, flushExcludedBreakouts, saveExcludedBreakouts]);

  const handleExcludedBreakoutsChange = useCallback(
    (nextIds) => {
      const next = nextIds.map(String);
      excludedRef.current = next;
      dirtyRef.current = true;
      setExcludedBreakoutIds(next);
      saveExcludedBreakouts();
    },
    [saveExcludedBreakouts]
  );

  const handleSave = useCallback(
    async (payload) => {
      saveExcludedBreakouts.cancel();
      try {
        const result = await updateRubric(rubricId, {
          ...payload,
          document: {
            ...rubric.document,
            ...payload.document,
            excluded_breakout_ids: excludedRef.current.map(String),
          },
        });
        dirtyRef.current = false;
        return result;
      } catch (err) {
        // Keep dirty so navigation still flushes exclusions.
        throw err;
      }
    },
    [updateRubric, rubricId, rubric, saveExcludedBreakouts]
  );

  if (!rubric && !rubricLoadFailed) {
    return <LoadingMessage iconSize="2x">Loading rubric…</LoadingMessage>;
  }

  if (!rubric) {
    return null;
  }

  return (
    <RubricSettingsComponent
      workspaceId={workspaceId}
      rubric={rubric}
      onSave={handleSave}
      breakouts={breakouts}
      breakoutsLoading={breakoutsLoading}
      excludedBreakoutIds={excludedBreakoutIds}
      onExcludedBreakoutsChange={handleExcludedBreakoutsChange}
    />
  );
}

export default connect(null, { updateRubric, listBreakouts })(
  RubricSettingsContainer
);
